import React, { useState, useCallback, useEffect } from 'react';
import './lew.css';
import esbdLogo from './logos/esbd-logo.png';
import ysjLogo from './logos/ysj-logo.png';
import globaraLogo from './logos/globara-logo.png';

/**
 * London Executive Leadership, Strategy & Culture Week 2026
 * European School of Business & Diplomacy
 *
 * Integration:
 *   import LondonExecutiveWeek from './london-executive-week/LondonExecutiveWeek';
 *   <LondonExecutiveWeek />                                   // same-site WordPress default
 *   <LondonExecutiveWeek endpoint="https://site/wp-admin/admin-ajax.php" />
 *
 * Registrations POST to the ESBD Executive Week plugin
 * (action=esbd_lew_register) and land in the delegate manager.
 */

/** Build stamp. Bump on every release so the deployed build is identifiable. */
export const LEW_BUILD = '1.1.0';

const TRACK1 = 'Track 1 — Global Business, Strategy and Responsible Leadership';
const TRACK2 = 'Track 2 — Transformational Public Leadership, Change and Cultural Agility';

/* ---------------------------------- data ---------------------------------- */

const DAYS = [
  { day: 'Day 1', date: 'Mon 23 Nov', title: 'Opening and first masterclass', text: 'Partner welcome, orientation and the first executive masterclass.' },
  { day: 'Day 2', date: 'Tue 24 Nov', title: 'Track teaching and case work', text: 'Track-based learning, applied frameworks, case studies and peer exchange.' },
  { day: 'Day 3', date: 'Wed 25 Nov', title: 'The leadership clinic', text: 'You put a real problem from your own job on the table, the room works on it, and you leave with an action plan for it.' },
  { day: 'Day 4', date: 'Thu 26 Nov', title: 'London institutional and cultural visit', text: "Out into the city, into the institutions where governance and public life happen. This is where the week's cultural strand lives: seeing how a different system works, up close." },
  { day: 'Day 5', date: 'Fri 27 Nov', title: 'The leadership forum', text: 'Global Leadership, Governance and Inclusive Growth Forum, certificate presentation and networking dinner.' },
];

const FAQS = [
  { q: 'Is this an accredited qualification?', a: 'No. It is a non-credit professional development certificate of participation. It is not an accredited degree, diploma, MQF-rated award or York St John University academic award.' },
  { q: 'What does the fee include?', a: "£2,800 covers three days of teaching, all materials, the certificate, six nights' accommodation, the London visit, the leadership forum, and the certificate presentation and dinner. Delegates who arrange their own accommodation pay £2,300. Flights, visa fees, airport transfers, travel insurance and personal expenses are not included." },
  { q: 'Where will teaching take place?', a: 'At York St John University London Campus, subject to final partner confirmation. The leadership forum venue will be confirmed to registered delegates closer to the date.' },
  { q: 'Can you help with a visa support letter?', a: 'Yes, we can provide a confirmation or visa support letter where agreed. Visa decisions rest with the relevant immigration authorities alone, and a support letter does not guarantee approval.' },
  { q: 'What happens if my visa is refused?', a: "You are covered. If your visa is refused, we refund your fee, less a £250 administrative charge and the bank's transfer charge (the bank charge is separate from the £250). Tell us on or before Monday 9 November 2026 and we recover the accommodation, so you get the full £2,800 back less those charges. Tell us after that date and the £500 accommodation cost is also deducted, as it will already have been paid. If you opted out of accommodation, no accommodation cost applies. An official refusal notice is required. This covers visa refusals only, not a change of mind or a missed deadline." },
  { q: 'Which track should I choose?', a: 'Track 1 suits private-sector and organisational strategy roles. Track 2 suits public-sector, government and change-leadership roles. You pick one at registration.' },
];

const AUDIENCE = [
  'Senior and middle managers', 'Government officials', 'Public-sector professionals',
  'NGO and civil society leaders', 'Entrepreneurs and business owners',
  'HR and organisational development professionals', 'Consultants and advisers',
  'Project and programme managers', 'Policy professionals',
  'Early-career leaders heading for senior responsibility',
];

const FALLBACK_MESSAGE = 'We could not reach the registration service. Please try again in a moment, or email admissions@eusbd.eu with your details and we will register you manually.';

const FORM_INIT = {
  fullname: '', email: '', phone: '', residence: '', nationality: '', org: '',
  jobtitle: '', track: '', category: '', experience: '', sector: '', visa: '',
  accom: '', heard: '', dietary: '', access: '', message: '', declaration: false,
  lew_hp_field: '', // honeypot — must stay empty
};

/* -------------------------------- component ------------------------------- */

export default function LondonExecutiveWeek({ endpoint = 'https://admissions.eusbd.eu/wp-admin/admin-ajax.php' }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null); // { reference } | null

  // Identifies the deployed build in the browser console. If this line is
  // absent on the live site, an older or hand-built page is deployed.
  useEffect(() => {
    console.info(`[London Executive Week] build ${LEW_BUILD} · registrations post to ${endpoint}`);
  }, [endpoint]);

  return (
    <div className="lew" data-lew-build={LEW_BUILD}>
      <Hero onRegister={() => setModalOpen(true)} />
      <Partners />
      <Overview />
      <Why />
      <Tracks />
      <Schedule />
      <Forum />
      <Fee onRegister={() => setModalOpen(true)} />
      <Audience />
      <Compliance />
      <Faq />
      <FooterCta onRegister={() => setModalOpen(true)} />
      <SiteFooter />
      {modalOpen && (
        <RegisterModal
          endpoint={endpoint}
          onClose={() => setModalOpen(false)}
          onSuccess={(reference) => {
            setModalOpen(false);
            setSuccessInfo({ reference });
          }}
        />
      )}
      {successInfo && (
        <SuccessModal
          reference={successInfo.reference}
          onClose={() => setSuccessInfo(null)}
        />
      )}
    </div>
  );
}

/* --------------------------------- sections -------------------------------- */

function Hero({ onRegister }) {
  return (
    <header className="lew-hero">
      <div className="lew-wrap lew-hero-grid">
        <div>
          <div className="lew-eyebrow">London · 23–27 November 2026</div>
          <h1>London Executive Leadership, Strategy &amp; <em>Culture</em> Week 2026</h1>
          <p className="lew-lede">A week in London, built for people who lead. Two certificate tracks, a day inside the city's institutions, and a leadership forum to close the week.</p>
          <div className="lew-cta-row">
            <button className="lew-btn lew-btn-gold" onClick={onRegister}>Register your interest</button>
            <a className="lew-btn lew-btn-ghost" href="#lew-tracks">See the two tracks</a>
          </div>
          <div className="lew-hero-meta">
            <div>Dates<strong>23–27 Nov 2026</strong></div>
            <div>Location<strong>London, UK</strong></div>
            <div>Fee<strong>£2,800 inc. accommodation · £2,300 without</strong></div>
          </div>
        </div>
        <div className="lew-hero-side">
          <div className="lew-hero-tag">Leadership · Strategy · Culture</div>
          <p>Five days: three of teaching and clinics, one out in London, one closing forum with a keynote, a panel, a masterclass and dinner.</p>
          <p>You leave with a sharper strategy lens, a working network across sectors and borders, and a plan written around your own job.</p>
        </div>
      </div>
    </header>
  );
}

function Partners() {
  return (
    <div className="lew-partners">
      <div className="lew-wrap">
        <span className="lew-partners-label">In collaboration with</span>
        <div className="lew-logo-tile"><img src={esbdLogo} alt="European School of Business and Diplomacy" /></div>
        <div className="lew-logo-tile"><img src={ysjLogo} alt="York St John University London Campus" /></div>
        <div className="lew-logo-tile"><img src={globaraLogo} alt="Globara UK" /></div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, children }) {
  return (
    <div className="lew-head-c">
      <div className="lew-eyebrow">{eyebrow}</div>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}

function Overview() {
  return (
    <section className="lew-section" id="lew-overview">
      <div className="lew-wrap">
        <SectionHead eyebrow="The programme" title="Five days in London, built for people who lead" />
        <div className="lew-lead-c">
          <p className="lew-lead-para">Most executive short courses keep people in one room for a week. This one does not. Across the five days, three go on structured teaching and applied work. A fourth moves out into London itself, into the institutions where governance and public life actually happen. The last day is a forum, part conference and part graduation, with a keynote, a panel, an executive masterclass, a delegate showcase, the certificate presentation and a networking dinner.</p>
          <p className="lew-lead-para">Culture is more the setting here than a single session. Delegates come from different sectors and countries, the London visit puts you inside institutions rather than reading about them, and Track 2 works directly on cultural agility. You reflect, argue, swap ideas and build something you can use when you get home.</p>
        </div>
      </div>
    </section>
  );
}

function Why() {
  const cards = [
    { n: '01', t: 'Applied, not theoretical', p: 'A leadership clinic where you put a real problem from your own job on the table. The room works on it. You leave with a plan for it.' },
    { n: '02', t: 'A day inside London', p: 'An institutional and cultural visit, not a slide about one. Leading across cultures lands harder when the difference is in front of you.' },
    { n: '03', t: 'A room worth being in', p: 'Delegates from public and private sectors, London and beyond. The network is half the value, and it outlasts the week.' },
  ];
  return (
    <section className="lew-section" id="lew-why">
      <div className="lew-wrap">
        <SectionHead eyebrow="Why attend" title="Not a week of watching slides" />
        <div className="lew-whygrid">
          {cards.map((c) => (
            <div className="lew-whycard" key={c.n}>
              <div className="lew-sq">{c.n}</div>
              <h3>{c.t}</h3>
              <p>{c.p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Tracks() {
  const t1 = ['Global strategy and competitive positioning', 'Ethical and responsible leadership', 'Governance, risk and institutional accountability', 'Innovation and organisational resilience', 'Deciding well when the picture is unclear', 'Building a practical strategy and leadership action plan'];
  const t2 = ['Transformational leadership for service delivery', 'Emotional intelligence and cultural agility', 'Stakeholder engagement and influence', 'Change leadership and getting it implemented', 'Trust, teamwork and difficult conversations', 'Tools for leading people and institutions through complexity'];
  return (
    <section className="lew-section lew-section-alt" id="lew-tracks">
      <div className="lew-wrap">
        <SectionHead eyebrow="Choose one" title="Two certificate tracks">
          Both tracks share the opening day, the London visit and the leadership forum. You pick one for the track-based sessions.
        </SectionHead>
        <div className="lew-trackwrap">
          <div className="lew-tcard lew-tcard-one">
            <span className="lew-badge">Track 1</span>
            <h3>Executive Certificate in Global Business, Strategy and Responsible Leadership</h3>
            <p className="lew-who">For business leaders, entrepreneurs, consultants, NGO leaders and professionals working across strategy, governance and organisational growth.</p>
            <ul>{t1.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
          <div className="lew-tcard lew-tcard-two">
            <span className="lew-badge">Track 2</span>
            <h3>Executive Certificate in Transformational Public Leadership, Change and Cultural Agility</h3>
            <p className="lew-who">For public-sector leaders, government officials, NGO executives, HR professionals, project managers and anyone running reform or change.</p>
            <ul>{t2.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Schedule() {
  return (
    <section className="lew-section" id="lew-schedule">
      <div className="lew-wrap">
        <SectionHead eyebrow="The five days" title="How the week runs">
          Arrive Sunday 22 November. Depart Saturday 28 November. Six nights in London.
        </SectionHead>
        <div className="lew-tl">
          {DAYS.map((d) => (
            <div className="lew-tlrow" key={d.day}>
              <div className="lew-when"><div className="lew-day">{d.day}</div><div className="lew-date">{d.date}</div></div>
              <div className="lew-what"><h3>{d.title}</h3><p>{d.text}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Forum() {
  return (
    <div className="lew-forum">
      <div className="lew-wrap">
        <div className="lew-eyebrow">Friday 27 November</div>
        <h2>The <span className="lew-g">Global Leadership, Governance and Inclusive Growth</span> Forum 2026</h2>
        <p>The week closes with a keynote, a panel, an executive masterclass, a delegate showcase, the certificate presentation and a networking dinner. Delegates sit alongside invited guests: public-sector leaders, business executives, NGO leaders, entrepreneurs, academics and diaspora leaders.</p>
        <p className="lew-forum-note">The venue will be confirmed to registered delegates. The forum may open to non-delegates through separate public registration, with pricing to be confirmed.</p>
      </div>
    </div>
  );
}

function Fee({ onRegister }) {
  const incl = ['Three days of teaching', 'All programme materials', 'The certificate', "Six nights' accommodation (optional)", 'London institutional visit', 'The leadership forum', 'Certificate presentation', 'Networking dinner'];
  return (
    <section className="lew-section lew-section-alt" id="lew-fee">
      <div className="lew-wrap">
        <SectionHead eyebrow="What it costs" title="The fee, and what it covers" />
        <div className="lew-feewrap">
          <div className="lew-pricebox">
            <div className="lew-price-big">£2,800</div>
            <div className="lew-price-per">per delegate</div>
            <div className="lew-price-accom">£2,800 includes six nights' accommodation, Sunday 22 to Saturday 28 November. £2,300 without, if you arrange your own.</div>
            <button className="lew-btn lew-btn-gold lew-btn-block" onClick={onRegister}>Register your interest</button>
          </div>
          <div className="lew-inclcard">
            <h3>What's included</h3>
            <div className="lew-inclgrid">{incl.map((x) => <div className="lew-i" key={x}>{x}</div>)}</div>
            <p className="lew-fee-excl"><b>Not included:</b> flights, visa fees, airport transfers, travel insurance and personal expenses.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Audience() {
  return (
    <section className="lew-section" id="lew-who">
      <div className="lew-wrap">
        <SectionHead eyebrow="Who it's for" title="Who should attend" />
        <div className="lew-aud">{AUDIENCE.map((x) => <div className="lew-aud-item" key={x}>{x}</div>)}</div>
      </div>
    </section>
  );
}

function Compliance() {
  return (
    <section className="lew-section lew-section-tight">
      <div className="lew-wrap">
        <div className="lew-compliance">
          <b>About the certificate.</b> Delegates who attend the required sessions and complete the programme activities receive an Executive Certificate in their chosen track. This is a non-credit professional development certificate of participation. It is not an MQF-rated award, does not carry ECTS credit, and does not constitute a York St John University academic award.
          <br /><br />
          <b>Visa refusal refund.</b> If your visa is refused, we refund your fee less a £250 administrative charge and the bank's transfer charge, which is separate from the £250. If you tell us on or before Monday 9 November 2026 we recover the accommodation and refund the full £2,800 less those charges; after that date the £500 accommodation cost is also deducted. Delegates who opt out of accommodation have no accommodation deduction. An official refusal notice is required. This covers visa refusals only.
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState(-1);
  return (
    <section className="lew-section lew-section-alt" id="lew-faq">
      <div className="lew-wrap">
        <SectionHead eyebrow="Questions" title="Frequently asked" />
        <div className="lew-faqwrap">
          {FAQS.map((f, i) => (
            <div className={'lew-faq-item' + (open === i ? ' lew-open' : '')} key={f.q}>
              <button className="lew-faq-q" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i}>
                {f.q}<span className="lew-plus">{open === i ? '−' : '+'}</span>
              </button>
              {open === i && <p className="lew-faq-a">{f.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FooterCta({ onRegister }) {
  return (
    <div className="lew-footcta">
      <div className="lew-wrap">
        <div className="lew-eyebrow">London · 23–27 November 2026</div>
        <h2>Spend a week leading in <em>London</em></h2>
        <p>Places are limited. Register your interest and join professionals from across the world.</p>
        <button className="lew-btn lew-btn-gold" onClick={onRegister}>Register your interest</button>
      </div>
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="lew-sitefoot">
      <div className="lew-wrap">
        <div>European School of Business &amp; Diplomacy · Malta</div>
        <div>Organised with York St John University London &amp; Globara UK</div>
      </div>
    </footer>
  );
}

/* ------------------------------ register modal ----------------------------- */

/* Defined outside RegisterModal so typing does not remount fields and steal focus. */
function Field({ k, label, required, errors, children }) {
  return (
    <div className={errors[k] ? 'lew-frow-item lew-field-bad' : 'lew-frow-item'}>
      <label>{label} {required && <span className="lew-req">*</span>}</label>
      {children}
      {errors[k] && <div className="lew-err">{errors[k]}</div>}
    </div>
  );
}

function RegisterModal({ endpoint, onClose, onSuccess }) {
  const [form, setForm] = useState(FORM_INIT);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('idle'); // idle | sending | fail
  const [feedback, setFeedback] = useState('');

  const set = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  function validate() {
    const e = {};
    if (!form.fullname.trim()) e.fullname = 'Please enter your full name.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = 'Please enter a valid email.';
    if (!form.phone.trim()) e.phone = 'Please enter your phone number.';
    if (!form.residence.trim()) e.residence = 'Please enter your country.';
    if (!form.track) e.track = 'Please choose a track.';
    if (!form.accom) e.accom = 'Please choose an accommodation option.';
    if (!form.declaration) e.declaration = 'Please confirm the declaration to continue.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function submit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setState('sending');
    setFeedback('');

    const fd = new FormData();
    fd.append('action', 'esbd_lew_register');
    Object.entries(form).forEach(([k, v]) => fd.append(k, k === 'declaration' ? (v ? '1' : '') : v));

    fetch(endpoint, { method: 'POST', body: fd })
      .then((r) => r.json().catch(() => null))
      .then((res) => {
        // A registration is only confirmed when the server returns a reference.
        // Without one, nothing was saved, so we must never show a success.
        const reference = res && res.success && res.data && res.data.reference;
        if (reference) {
          onSuccess(reference);
          return;
        }
        // Genuine rejection (duplicate email, rate limit, validation): show the
        // server's reason. Keep the form open so values are not lost.
        const rejected = res && res.success === false && res.data && res.data.message;
        setState('fail');
        setFeedback(rejected ? res.data.message : FALLBACK_MESSAGE);
        if (!rejected) diagnose();
      })
      .catch(() => {
        setState('fail');
        setFeedback(FALLBACK_MESSAGE);
        diagnose();
      });
  }

  /** Logs a precise cause to the console when a submission fails. */
  function diagnose() {
    fetch(`${endpoint}?action=esbd_lew_ping`)
      .then((r) => r.json())
      .then((p) => {
        if (p && p.ok) {
          console.error('[London Executive Week] The plugin is reachable (v' + p.version + ') but the registration was rejected. Check the field names sent against the plugin contract.');
        } else {
          console.error('[London Executive Week] Unexpected response from the registration endpoint.', p);
        }
      })
      .catch(() => {
        console.error(`[London Executive Week] Cannot reach ${endpoint}. Likely causes: wrong endpoint URL, or this origin (${window.location.origin}) is not listed under Executive Week > Settings > Cross-domain registrations in WordPress.`);
      });
  }

  return (
    <div className="lew-modal-back">
      <div className="lew-modal" role="dialog" aria-modal="true" aria-labelledby="lew-m-title">
        <div className="lew-modal-head">
          <div>
            <h3 id="lew-m-title">Register your interest</h3>
            <p>London Executive Week 2026 · 23–27 November</p>
          </div>
          <button className="lew-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="lew-modal-body">
          {state === 'fail' && <div className="lew-msg lew-msg-fail">{feedback}</div>}
          <form onSubmit={submit} noValidate>
            {/* honeypot — invisible to people, bots fill it */}
            <div className="lew-hp" aria-hidden="true">
              <label>Website</label>
              <input type="text" tabIndex="-1" autoComplete="off" value={form.lew_hp_field} onChange={(e) => set('lew_hp_field', e.target.value)} />
            </div>

            <div className="lew-frow lew-two">
              <Field k="fullname" label="Full name" required errors={errors}><input value={form.fullname} onChange={(e) => set('fullname', e.target.value)} /></Field>
              <Field k="email" label="Email address" required errors={errors}><input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
            </div>
            <div className="lew-frow lew-two">
              <Field k="phone" label="Phone (with country code)" required errors={errors}><input placeholder="+44 …" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
              <Field k="residence" label="Country of residence" required errors={errors}><input value={form.residence} onChange={(e) => set('residence', e.target.value)} /></Field>
            </div>
            <div className="lew-frow lew-two">
              <Field k="nationality" label="Nationality" errors={errors}><input value={form.nationality} onChange={(e) => set('nationality', e.target.value)} /></Field>
              <Field k="org" label="Organisation / employer" errors={errors}><input value={form.org} onChange={(e) => set('org', e.target.value)} /></Field>
            </div>
            <div className="lew-frow lew-two">
              <Field k="jobtitle" label="Job title" errors={errors}><input value={form.jobtitle} onChange={(e) => set('jobtitle', e.target.value)} /></Field>
              <Field k="track" label="Preferred track" required errors={errors}>
                <select value={form.track} onChange={(e) => set('track', e.target.value)}>
                  <option value="">Select a track…</option>
                  <option>{TRACK1}</option>
                  <option>{TRACK2}</option>
                </select>
              </Field>
            </div>
            <div className="lew-frow lew-two">
              <Field k="category" label="Participant category" errors={errors}>
                <select value={form.category} onChange={(e) => set('category', e.target.value)}>
                  <option value="">Select…</option>
                  {['Business leader / entrepreneur', 'Government official', 'Public-sector professional', 'NGO / civil society leader', 'HR / organisational development', 'Project or programme manager', 'Consultant / adviser', 'Academic / researcher', 'Early-career professional', 'Other'].map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>
              <Field k="experience" label="Level of experience" errors={errors}>
                <select value={form.experience} onChange={(e) => set('experience', e.target.value)}>
                  <option value="">Select…</option>
                  {['0–2 years', '3–5 years', '6–10 years', '11–15 years', '16 years or more'].map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>
            </div>
            <div className="lew-frow lew-two">
              <Field k="sector" label="Sector" errors={errors}><input value={form.sector} onChange={(e) => set('sector', e.target.value)} /></Field>
              <Field k="visa" label="Confirmation / visa support letter?" errors={errors}>
                <select value={form.visa} onChange={(e) => set('visa', e.target.value)}>
                  <option value="">Select…</option><option>Yes</option><option>No</option><option>Not sure yet</option>
                </select>
                <div className="lew-hint">If your visa is later refused, our refund policy applies. See the FAQ for full terms.</div>
              </Field>
            </div>
            <div className="lew-frow lew-two">
              <Field k="accom" label="Accommodation" required errors={errors}>
                <select value={form.accom} onChange={(e) => set('accom', e.target.value)}>
                  <option value="">Select…</option>
                  <option>Include 6 nights' accommodation (£2,800)</option>
                  <option>No accommodation, I will arrange my own (£2,300)</option>
                </select>
              </Field>
              <Field k="heard" label="How did you hear about us?" errors={errors}>
                <select value={form.heard} onChange={(e) => set('heard', e.target.value)}>
                  <option value="">Select…</option>
                  {['LinkedIn', 'Email', 'Colleague or referral', 'ESBD website', 'Globara UK', 'Other'].map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>
            </div>
            <div className="lew-frow lew-two">
              <Field k="dietary" label="Dietary requirements" errors={errors}><input value={form.dietary} onChange={(e) => set('dietary', e.target.value)} /></Field>
              <Field k="access" label="Accessibility requirements" errors={errors}><input value={form.access} onChange={(e) => set('access', e.target.value)} /></Field>
            </div>
            <div className="lew-frow">
              <Field k="message" label="Short message or enquiry" errors={errors}><textarea rows="3" value={form.message} onChange={(e) => set('message', e.target.value)} /></Field>
            </div>
            <div className={errors.declaration ? 'lew-frow lew-field-bad' : 'lew-frow'}>
              <div className="lew-checkrow">
                <input type="checkbox" id="lew-decl" checked={form.declaration} onChange={(e) => set('declaration', e.target.checked)} />
                <label htmlFor="lew-decl">I understand that this is a non-credit professional development programme leading to an executive certificate. It is not an accredited degree, diploma, MQF-rated award or York St John University academic award.</label>
              </div>
              {errors.declaration && <div className="lew-err">{errors.declaration}</div>}
            </div>
            <button type="submit" className="lew-btn lew-btn-gold lew-btn-block lew-btn-submit" disabled={state === 'sending'}>
              {state === 'sending' ? 'Submitting…' : 'Submit registration'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function SuccessModal({ reference, onClose }) {
  return (
    <div className="lew-modal-back">
      <div className="lew-modal lew-success-modal" role="dialog" aria-modal="true" aria-labelledby="lew-success-title">
        <div className="lew-modal-head">
          <div>
            <h3 id="lew-success-title">Registration received</h3>
            <p>London Executive Week 2026 · 23–27 November</p>
          </div>
          <button className="lew-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="lew-modal-body">
          <div className="lew-msg lew-msg-ok">
            Thank you. Your registration has been received.
            {reference ? <> Your reference is <strong>{reference}</strong>.</> : null}
            {' '}A confirmation email is on its way.
          </div>
          <button type="button" className="lew-btn lew-btn-gold lew-btn-block" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
